/**
 * Week/date filter: pill-shaped button showing "Week 47 - Nov 2025" or "All data",
 * with calendar icon and chevron. Dropdown to select a week or "All data".
 * Filters dashboard data by the selected range.
 */

import PropTypes from "prop-types";
import { useState } from "react";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import { useTheme } from "@mui/material";
import {
  getISOWeek,
  getLastWeeks,
  formatWeekLabel,
} from "../../utils/weekFilter";

const TEAL_BG = "#34ac8f";

function WeekDateFilter({ value, onChange, buttonBg }) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const bg = buttonBg ?? theme.palette?.statusSbi?.success ?? TEAL_BG;

  const isAll = value?.type === "all";
  const label = isAll
    ? "All data"
    : value?.type === "week" && value?.week != null && value?.year != null
    ? formatWeekLabel(value.week, value.year)
    : (() => {
        const { week, year } = getISOWeek(new Date());
        return formatWeekLabel(week, year);
      })();

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelectAll = () => {
    onChange({ type: "all" });
    handleClose();
  };

  const handleSelectWeek = (week, year) => {
    onChange({ type: "week", week, year });
    handleClose();
  };

  const weekOptions = getLastWeeks(12);

  return (
    <>
      <MDBox
        component="button"
        onClick={handleOpen}
        display="flex"
        alignItems="center"
        gap={1.25}
        sx={{
          padding: "10px 16px",
          borderRadius: "9999px",
          border: "none",
          cursor: "pointer",
          backgroundColor: bg,
          color: "#fff",
          fontWeight: 600,
          fontSize: "0.875rem",
          "&:hover": {
            opacity: 0.92,
            backgroundColor: bg,
          },
        }}
      >
        <Icon sx={{ fontSize: "1.25rem", color: "#fff" }}>calendar_today</Icon>
        <MDTypography variant="button" fontWeight="600" sx={{ color: "#fff" }}>
          {label}
        </MDTypography>
        <Icon sx={{ fontSize: "1.125rem", color: "#fff" }}>
          keyboard_arrow_down
        </Icon>
      </MDBox>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 220,
            borderRadius: 2,
            boxShadow: 4,
          },
        }}
      >
        <MenuItem
          onClick={handleSelectAll}
          selected={isAll}
          sx={{
            fontWeight: isAll ? 600 : 400,
          }}
        >
          <ListItemText primary="All data" />
        </MenuItem>
        {weekOptions.map(({ week, year, label: weekLabel }) => (
          <MenuItem
            key={`${year}-W${week}`}
            onClick={() => handleSelectWeek(week, year)}
            selected={
              !isAll &&
              value?.type === "week" &&
              value?.week === week &&
              value?.year === year
            }
            sx={{
              fontWeight:
                !isAll && value?.week === week && value?.year === year
                  ? 600
                  : 400,
            }}
          >
            <ListItemText primary={weekLabel} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

WeekDateFilter.defaultProps = {
  value: { type: "all" },
  onChange: () => {},
  buttonBg: undefined,
};

WeekDateFilter.propTypes = {
  value: PropTypes.shape({
    type: PropTypes.oneOf(["all", "week"]),
    week: PropTypes.number,
    year: PropTypes.number,
  }),
  onChange: PropTypes.func,
  buttonBg: PropTypes.string,
};

export default WeekDateFilter;
